import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, MapPin, Home, Building2, Satellite, Map } from "lucide-react";
import { cn } from "@/lib/utils";

import {
  GoogleMap,
  Marker,
  InfoWindow,
  Polyline,
  useJsApiLoader,
} from "@react-google-maps/api";

type LocationData = {
  home_latitude?: number | string;
  home_longitude?: number | string;
  business_latitude?: number | string;
  business_longitude?: number | string;
  fname?: string;
  sname?: string;
  businessName?: string;
};

const containerStyle: React.CSSProperties = {
  width: "100%",
  height: "420px",
  borderRadius: "16px",
};

function toNum(v: any): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function hasAllCoords(d: LocationData | null) {
  const hl = toNum(d?.home_latitude);
  const hlg = toNum(d?.home_longitude);
  const bl = toNum(d?.business_latitude);
  const blg = toNum(d?.business_longitude);
  return hl !== null && hlg !== null && bl !== null && blg !== null;
}

/** Simple SVG marker icons (data URI) */
function svgPin(fill: string) {
  const svg = encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 24 24">
      <path fill="${fill}" d="M12 2c3.314 0 6 2.686 6 6c0 4.5-6 14-6 14S6 12.5 6 8c0-3.314 2.686-6 6-6z"/>
      <circle cx="12" cy="8" r="2.5" fill="white"/>
    </svg>
  `);
  return `data:image/svg+xml;charset=UTF-8,${svg}`;
}

export default function CustomerLocationTab({ customerId }: { customerId: string }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState<LocationData | null>(null);
  const [activeMarker, setActiveMarker] = useState<"home" | "business" | null>(null);

  const [mapType, setMapType] = useState<"roadmap" | "hybrid">("roadmap");

  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAP_KEY,
  });

  async function load() {
    if (!customerId) return;

    setLoading(true);
    setErr("");

    try {
      const res = await apiFetch<any>(`/customer-profile/${customerId}/location`);
      const payload = res?.data ?? res;
      const customer = payload?.data ?? payload;

      setData(customer);
    } catch (e: any) {
      setErr(e?.message || "Failed to load location");
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const homePos = useMemo(() => {
    if (!data) return null;
    const lat = toNum(data.home_latitude);
    const lng = toNum(data.home_longitude);
    if (lat === null || lng === null) return null;
    return { lat, lng };
  }, [data]);

  const businessPos = useMemo(() => {
    if (!data) return null;
    const lat = toNum(data.business_latitude);
    const lng = toNum(data.business_longitude);
    if (lat === null || lng === null) return null;
    return { lat, lng };
  }, [data]);

  /** Auto-fit bounds whenever coords ready */
  useEffect(() => {
    if (!mapRef.current) return;
    if (!homePos || !businessPos) return;

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(homePos);
    bounds.extend(businessPos);
    mapRef.current.fitBounds(bounds, 60);
  }, [homePos, businessPos]);

  /** Fallback center if fitBounds not ready */
  const center = useMemo(() => homePos ?? { lat: 0, lng: 0 }, [homePos]);

  const okCoords = hasAllCoords(data);

  if (loading || !isLoaded) {
    return (
      <Card className="shadow-card">
        <CardContent className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading map...
        </CardContent>
      </Card>
    );
  }

  if (err) {
    return (
      <Card className="shadow-card">
        <CardContent className="p-4 space-y-3">
          <div className="text-sm text-destructive">{err}</div>
          <Button variant="secondary" onClick={load}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!okCoords || !homePos || !businessPos) {
    return (
      <Card className="shadow-card">
        <CardContent className="p-4 text-sm text-muted-foreground">
          No location coordinates found for this customer.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-card">
      <CardContent className="p-4 space-y-3">
        {/* Header / Controls */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm font-semibold inline-flex items-center gap-2">
            <MapPin className="h-4 w-4 text-primary" />
            Customer Location
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant={mapType === "roadmap" ? "default" : "secondary"}
              onClick={() => setMapType("roadmap")}
              className="gap-2"
            >
              <Map className="h-4 w-4" />
              Map
            </Button>

            <Button
              type="button"
              size="sm"
              variant={mapType === "hybrid" ? "default" : "secondary"}
              onClick={() => setMapType("hybrid")}
              className="gap-2"
            >
              <Satellite className="h-4 w-4" />
              Satellite
            </Button>
          </div>
        </div>

        {/* Map */}
        <div className="rounded-2xl overflow-hidden border">
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={15}
            onLoad={(map) => {
              mapRef.current = map;
              const bounds = new google.maps.LatLngBounds();
              bounds.extend(homePos);
              bounds.extend(businessPos);
              map.fitBounds(bounds, 60);
            }}
            options={{
              mapTypeId: mapType,
              fullscreenControl: false,
              streetViewControl: false,
              mapTypeControl: false,
              clickableIcons: false,
              gestureHandling: "greedy",
            }}
          >
            {/* Route line */}
            <Polyline
              path={[homePos, businessPos]}
              options={{
                strokeOpacity: 0.9,
                strokeWeight: 4,
                geodesic: true,
              }}
            />

            {/* Home marker */}
            <Marker
              position={homePos}
              icon={{
                url: svgPin("#2563EB"), // blue-ish
                scaledSize: new google.maps.Size(40, 40),
              }}
              onClick={() => setActiveMarker("home")}
            />

            {activeMarker === "home" && (
              <InfoWindow position={homePos} onCloseClick={() => setActiveMarker(null)}>
                <div className="text-sm">
                  <div className="font-semibold flex items-center gap-1">
                    <Home className={cn("h-4 w-4")} />
                    Home Location
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {data?.fname} {data?.sname}
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    {homePos.lat.toFixed(6)}, {homePos.lng.toFixed(6)}
                  </div>
                </div>
              </InfoWindow>
            )}

            {/* Business marker */}
            <Marker
              position={businessPos}
              icon={{
                url: svgPin("#16A34A"), // green-ish
                scaledSize: new google.maps.Size(40, 40),
              }}
              onClick={() => setActiveMarker("business")}
            />

            {activeMarker === "business" && (
              <InfoWindow position={businessPos} onCloseClick={() => setActiveMarker(null)}>
                <div className="text-sm">
                  <div className="font-semibold flex items-center gap-1">
                    <Building2 className="h-4 w-4" />
                    Business
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {data?.businessName || "-"}
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    {businessPos.lat.toFixed(6)}, {businessPos.lng.toFixed(6)}
                  </div>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </div>

        {/* Footer info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-xl border bg-muted/20 p-3">
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Home className="h-3.5 w-3.5 text-primary" /> Home Coordinates
            </div>
            <div className="text-sm font-semibold">
              {homePos.lat.toFixed(6)}, {homePos.lng.toFixed(6)}
            </div>
          </div>

          <div className="rounded-xl border bg-muted/20 p-3">
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Building2 className="h-3.5 w-3.5 text-primary" /> Business Coordinates
            </div>
            <div className="text-sm font-semibold">
              {businessPos.lat.toFixed(6)}, {businessPos.lng.toFixed(6)}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}